/**
 * 通用的分页全量导出工具：
 * - 第 1 页拿 total，后续分页通过有限并发拉取
 * - 针对瞬时错误（502/503/504/网络层 statusCode=0）指数退避重试
 * - 部分分页失败时回调 onConfirmPartial，由调用方决定是否仅导出已获取部分
 */

// 视为可重试的瞬时错误码（statusCode=0 表示请求未拿到完整 HTTP 响应，常见于 CORS/网络中断）
const TRANSIENT_CODES = new Set([0, 502, 503, 504]);

export interface ExportPaginatedOptions<T> {
  /** 拉取指定分页，返回标准化的 { items, total } */
  fetchPage: (page: number, size: number) => Promise<{ items: T[]; total: number } | null | undefined>;
  /** 单页大小，默认 500 */
  pageSize?: number;
  /** 最大在途并发数，默认 4 */
  concurrency?: number;
  /** 单页失败重试次数（不含首次），默认 2 */
  maxRetries?: number;
  /** 部分分页失败时的确认回调；返回 false 视为放弃导出 */
  onConfirmPartial?: (fetched: number, total: number) => Promise<boolean>;
}

export interface ExportPaginatedResult<T> {
  items: T[];
  total: number;
  /** 是否为部分导出（有分页彻底失败但用户选择继续） */
  partial: boolean;
}

const getErrorCode = (err: unknown): number => {
  const e = err as { statusCode?: number; response?: { status?: number } } | null;
  return e?.statusCode ?? e?.response?.status ?? 0;
};

/**
 * 拉取全部分页数据。返回 null 表示首页失败或用户取消，调用方应据此直接退出导出流程。
 */
export async function exportPaginatedData<T>(
  options: ExportPaginatedOptions<T>
): Promise<ExportPaginatedResult<T> | null> {
  const { fetchPage, pageSize = 500, concurrency = 4, maxRetries = 2, onConfirmPartial } = options;

  const fetchPageWithRetry = async (page: number) => {
    let lastError: unknown;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fetchPage(page, pageSize);
      } catch (err) {
        lastError = err;
        const code = getErrorCode(err);
        if (!TRANSIENT_CODES.has(code) || attempt === maxRetries) throw err;
        // 指数退避：500ms → 1s → 2s
        await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
      }
    }
    throw lastError;
  };

  // 第 1 页失败直接抛错给调用方
  const firstRes = await fetchPageWithRetry(1);
  if (!firstRes || !Array.isArray(firstRes.items)) return null;

  const { total } = firstRes;
  const allItems = [...firstRes.items];

  if (total <= pageSize) {
    return { items: allItems, total, partial: false };
  }

  const totalPages = Math.ceil(total / pageSize);
  const tasks: (() => ReturnType<typeof fetchPageWithRetry>)[] = [];
  for (let i = 2; i <= totalPages; i++) {
    const page = i;
    tasks.push(() => fetchPageWithRetry(page));
  }

  // 简易并发池
  const results: (
    | { ok: true; value: Awaited<ReturnType<typeof fetchPageWithRetry>> }
    | { ok: false; error: unknown }
  )[] = new Array(tasks.length);
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(concurrency, tasks.length) }, async () => {
      while (true) {
        const i = cursor++;
        if (i >= tasks.length) return;
        try {
          results[i] = { ok: true, value: await tasks[i]() };
        } catch (e) {
          results[i] = { ok: false, error: e };
        }
      }
    })
  );

  let failedCount = 0;
  results.forEach((r) => {
    if (r.ok) {
      const data = r.value;
      if (data && Array.isArray(data.items)) allItems.push(...data.items);
    } else {
      failedCount++;
      console.warn('exportPaginatedData: page fetch failed after retries:', r.error);
    }
  });

  if (failedCount === 0) {
    return { items: allItems, total, partial: false };
  }

  if (!onConfirmPartial) {
    console.warn('exportPaginatedData: partial failure without onConfirmPartial handler; aborting');
    return null;
  }
  const proceed = await onConfirmPartial(allItems.length, total);
  if (!proceed) return null;
  return { items: allItems, total, partial: true };
}
